import React, { useState, useMemo, useRef, useEffect } from 'react';

// ux
import LoadingAnimation from '../ux/LoadingAnimation.js';
import PitchListItem from '../ux/PitchListItem.js';

const Pitches = ({
  pitches,
  dashboardLoading,
  handleOpenModal,
  handleViewChange,
  renamePitch
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('date');
  const [selectedPitches, setSelectedPitches] = useState([]);

  const selectAllRef = useRef();

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  const handlePitchSelect = (pitchId) => {
    setSelectedPitches((prevSelected) => {
      if (prevSelected.includes(pitchId)) {
        // If already selected, remove it
        return prevSelected.filter((id) => id !== pitchId);
      } else {
        // If not selected, add it
        return [...prevSelected, pitchId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPitches.length === sortedFilteredPitches.length) {
      // If all are selected, deselect all
      setSelectedPitches([]);
    } else {
      // If not all are selected, select all
      const allPitchIds = sortedFilteredPitches.map((pitch) => pitch.ID);
      setSelectedPitches(allPitchIds);
    }
  };

  const sortedFilteredPitches = useMemo(() => {
    // Step 4.1: Filter fuses based on the search term
    let filtered = pitches;
    if (searchTerm) {
      filtered = pitches.filter((pitch) =>
        pitch.pitch_title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Step 4.2: Sort the filtered projects based on the selected sort option
    let sorted = [...filtered]; // Create a shallow copy to avoid mutating the original array

    if (sortOption === 'date') {
      // Assuming 'date' is a valid date string or Date object in your project
      sorted.sort((a, b) => new Date(a.date) - new Date(b.date)); // Ascending order (oldest first)
      // For descending order (newest first), use: new Date(b.date) - new Date(a.date)
    } else if (sortOption === 'name') {
      sorted.sort((a, b) => a.pitch_title.localeCompare(b.pitch_title));
    }

    return sorted;
  }, [searchTerm, sortOption, pitches]);

  useEffect(() => {
    if (
      selectedPitches.length > 0 &&
      selectedPitches.length < sortedFilteredPitches.length
    ) {
      selectAllRef.current.indeterminate = true;
    } else {
      selectAllRef.current.indeterminate = false;
    }
  }, [selectedPitches, sortedFilteredPitches.length]);

  return (
    <div className="fuselight-dashboard-container">
      <div className="dashboard-bg">
        <div></div>
        <div></div>
        <div></div>
      </div>
      <div className="search-and-sort-container">
        <div className="sort-container">
          <label htmlFor="sort-select" className="sort-label">
            Sort By:
          </label>
          <select
            id="sort-select"
            value={sortOption}
            onChange={handleSortChange}
            className="sort-select"
            aria-label="Sort Pitches"
          >
            <option value="date">Recent</option>
            <option value="name">Title</option>
          </select>
        </div>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
            aria-label="Search Pitches"
          />
        </div>
        <button
          className="delete-button"
          onClick={() => handleOpenModal('deletePitches', selectedPitches)}
          disabled={selectedPitches.length === 0}
        >
          <i class="fas fa-trash-alt"></i>
        </button>
      </div>
      <h3>All Pitches</h3>
      <div className="fuselight-dashboard-header">
        <div className="fuselight-dashboard-header-checkbox">
          <label className="custom-checkbox">
            <input
              type="checkbox"
              ref={selectAllRef}
              checked={
                selectedPitches.length === sortedFilteredPitches.length &&
                sortedFilteredPitches.length > 0
              }
              onChange={handleSelectAll}
              aria-label="Select All Pitches"
            />
            <span></span>
          </label>
        </div>
        <h4>Title</h4>
        <h4>Fusions</h4>
        <h4>Date</h4>
      </div>
      <div className="fuselight-dashboard-content">
        {dashboardLoading ? (
          <LoadingAnimation label="Loading Pitches" />
        ) : (
          <>
            {sortedFilteredPitches.length > 0 ? (
              <div className="fuselight-dashboard-list">
                {sortedFilteredPitches.map((pitch) => (
                  <PitchListItem
                    key={pitch.ID}
                    pitch={pitch}
                    handleOpenModal={handleOpenModal}
                    isSelected={selectedPitches.includes(pitch.ID)}
                    onSelectChange={() => handlePitchSelect(pitch.ID)}
                    renamePitch={renamePitch}
                  />
                ))}
              </div>
            ) : (
              <div className="dashboard-none-found">
                <p>No pitches found</p>
                <button
                  className="btn btn--secondary-outline"
                  onClick={() => handleViewChange('launchpad')}
                >
                  Create One
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Pitches;
